// Journal › Dashboard: the performance overview for the account picked in the sidebar
// (rules panel, stat tiles, charts, the top insight, the P&L calendar, and the best,
// worst and not-yet-journaled trades).
import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AccountOverview, AccountStatusCard } from './AccountStatus';
import Calendar from './Calendar';
import DashboardWidgets from './DashboardWidgets';
import MissingFeesNote from './MissingFees';
import { dayKey, formatMoney, netPnl } from './pnl';
import { tradeTime } from './tradeTime';
import { unjournaledTrades } from './stats';
import { currentLocale } from './langState';
import { useI18n } from './i18n';
import { canSeeView, featureOpen } from './AppNav';

// One compact list of trades: date and symbol on the left, net P&L on the right.
function TradeList({ trades, onEditTrade, empty }) {
  const { t } = useI18n();
  if (!trades.length) return <p className="empty">{empty}</p>;
  return (
    <ul className="trade-mini-list">
      {trades.map((trade) => {
        const net = netPnl(trade);
        return (
          <li key={trade._id}>
            <button type="button" className="trade-mini-row" onClick={() => onEditTrade(trade)} title={t('Edit, tag or delete this trade')}>
              <span className="trade-mini-main">
                <strong>{trade.symbol}</strong> <span className="trade-mini-side">{t(trade.side || 'Long')} &times; {trade.size || 1}</span>
                <small>
                  {new Date(trade.date).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' })}{' '}
                  {tradeTime(trade)}
                </small>
              </span>
              <span className={`trade-mini-pnl ${net > 0 ? 'gain' : net < 0 ? 'loss' : ''}`}>{formatMoney(net)}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

// Below the calendar: the best trades, the worst trades, and the ones still waiting
// for tags or notes, side by side.
function TradeLists({ trades, onEditTrade }) {
  const { t } = useI18n();
  const byNet = [...trades].sort((a, b) => netPnl(b) - netPnl(a));
  const best = byNet.filter((x) => netPnl(x) > 0).slice(0, 5);
  const worst = byNet.filter((x) => netPnl(x) < 0).reverse().slice(0, 5);
  const waiting = unjournaledTrades(trades);
  return (
    <section className="trade-lists">
      <div className="dash-card-head">
        <h2>{t('Your trades')}</h2>
        <Link to="/app" className="home-link">{t('All trades')} &rarr;</Link>
      </div>
      <div className="trade-lists-grid">
        <div className="dash-card">
          <h3 className="trade-list-title gain">{t('Best trades')}</h3>
          <TradeList trades={best} onEditTrade={onEditTrade} empty={t('No winning trades yet.')} />
        </div>
        <div className="dash-card">
          <h3 className="trade-list-title loss">{t('Worst trades')}</h3>
          <TradeList trades={worst} onEditTrade={onEditTrade} empty={t('No losing trades yet.')} />
        </div>
        <div className="dash-card">
          <h3 className="trade-list-title">
            {t('Waiting for tags or notes')}
            {waiting.length > 0 && <span className="trade-list-count">{waiting.length}</span>}
          </h3>
          <TradeList trades={waiting.slice(0, 5)} onEditTrade={onEditTrade} empty={t('Every trade has tags or notes. Nice work.')} />
          {waiting.length > 5 && (
            <p className="trade-list-more">{t('Showing the latest 5. Click a trade to tag it or add a note.')}</p>
          )}
        </div>
      </div>
    </section>
  );
}

// Shown instead of empty charts until there are trades to measure.
function DashboardWelcome({ hasAccount, hasOtherTrades, onAddTrade, onManageAccounts }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  return (
    <section className="dash-card dash-welcome">
      <h2>{hasOtherTrades ? t('No trades in this account yet') : t('Your dashboard fills in from your trades')}</h2>
      <p>{t('Import a file from your platform or add a trade by hand. Your stats, charts, calendar and insights update as soon as you do.')}</p>
      <div className="dash-welcome-actions">
        {!hasAccount && <button type="button" onClick={onManageAccounts}>{t('Set up your account')}</button>}
        <button type="button" className={hasAccount ? '' : 'secondary'} onClick={() => navigate('/app', { state: { scrollTo: 'import' } })}>{t('Import your trades')}</button>
        <button type="button" className="secondary" onClick={() => onAddTrade(dayKey(new Date()))}>{t('Add a trade by hand')}</button>
      </div>
    </section>
  );
}

export default function DashboardPage({ trades, visibleTrades, accounts, selected, library, scope, notes, setNotes, onSelectAccount, onManageAccounts,
  onEditTrade, onAddTrade, onTradesUpdated, onShare, onSessionExpired, onAccountChange, shownIds, displaySize, accountTrades, toolbar, filterNote, scopeLine, narrowed, user, onUserChange }) {
  const { t } = useI18n();
  const account = selected !== 'all' ? accounts.find((a) => a._id === selected) || null : null;
  // Day insights on the calendars: open to members from Admin → Features; until then admins see them in gold.
  const dayInsightsOpen = featureOpen(user, 'dayInsights');
  const insights = useMemo(() => (dayInsightsOpen || (user && user.isAdmin)
    ? { preview: !dayInsightsOpen, mark: canSeeView(user, 'mark'), accounts } : null), [dayInsightsOpen, user, accounts]);
  return (
    <div className="dashboard-page">
      <div className="page-title page-title-row">
        <div>
          <h1>{t('Dashboard')}</h1>
          <p className="subtitle">{scope}</p>
        </div>
        {visibleTrades.length > 0 && (
          <button type="button" className="page-action share-button" onClick={onShare}>{t('Share my results')}</button>
        )}
      </div>
      {toolbar}
      {filterNote}

      <MissingFeesNote trades={accountTrades} allTrades={trades} accounts={accounts} onUpdated={onTradesUpdated} onAccountSaved={onAccountChange} onSessionExpired={onSessionExpired} />

      {visibleTrades.length > 0 ? (
        // With trades, the Dashboard is widgets (DashboardWidgets.js): the classic layout unless
        // the member customized it.
        <DashboardWidgets user={user} onUserChange={onUserChange} ctx={{
          trades: visibleTrades, accountTrades, allTrades: trades, accounts, account, shownIds, size: displaySize, library, notes, setNotes,
          narrowed, scopeLine, onEditTrade, onAddTrade, onManageAccounts, onAccountChange, onSelectAccount, onSessionExpired, TradeLists, insights,
        }} />
      ) : (
        <>
          {account ? (
            <AccountStatusCard account={account} trades={accountTrades} onManage={() => onManageAccounts(account._id)}
              onAccountChange={onAccountChange} onSessionExpired={onSessionExpired} />
          ) : accounts.some((a) => !a.archived) ? (
            <AccountOverview accounts={shownIds ? accounts.filter((a) => shownIds.includes(a._id)) : accounts} trades={trades} onSelect={onSelectAccount} />
          ) : null}
          {accountTrades.length > 0 ? (
            <section className="dash-card dash-welcome">
              <h2>{t('No trades match these filters')}</h2>
              <p>{t('Change the filters or the date range at the top to see more trades.')}</p>
            </section>
          ) : (
            <DashboardWelcome hasAccount={accounts.some((a) => !a.archived)} hasOtherTrades={trades.length > 0}
              onAddTrade={onAddTrade} onManageAccounts={onManageAccounts} />
          )}
          <Calendar trades={visibleTrades} account={account} size={displaySize} library={library} notes={notes} setNotes={setNotes} insights={insights}
            onEditTrade={onEditTrade} onAddTrade={onAddTrade} onSessionExpired={onSessionExpired} />
        </>
      )}
    </div>
  );
}
